import axios from "axios";
import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Table, Spin, Typography, message, Select, Space, Button } from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { Title } = Typography;
const { Option } = Select;

export default function AppraisalEmployees({ SelectedAppId }) {
    const { authData, backUrl } = useContext(AuthContext);
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedEmpIds, setSelectedEmpIds] = useState([]);
    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    // ✅ Fetch all organisations
    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganisations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching organizations:", error);
        }
    };

    // ✅ Fetch departments when organisation changes
    const fetchDepartments = async (orgId) => {
        if (!orgId) {
            setDepartments([]);
            return;
        }
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setDepartments(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    // ✅ Fetch designations when department changes
    const fetchDesignations = async (deptId) => {
        if (!deptId) {
            setDesignations([]);
            return;
        }
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching designations:", error);
        }
    };

    // ✅ Fetch all employees
    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/emp/employees`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const empList = res.data.results || [];
            setEmployees(empList);
            setFilteredEmployees(empList);
        } catch (error) {
            console.error("Error fetching employees:", error);
            message.error("Failed to fetch employee data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Filter logic
    useEffect(() => {
        let filtered = [...employees];
        if (selectedOrg)
            filtered = filtered.filter(
                (emp) => emp.organisation === selectedOrg || emp.organisation_name_id === selectedOrg
            );
        if (selectedDept)
            filtered = filtered.filter(
                (emp) => emp.department === selectedDept || emp.department_name_id === selectedDept
            );
        if (selectedDesig)
            filtered = filtered.filter(
                (emp) => emp.designation === selectedDesig || emp.designation_name_id === selectedDesig
            );
        setFilteredEmployees(filtered);
    }, [selectedOrg, selectedDept, selectedDesig, employees]);

    // ✅ Filter handlers
    const handleOrgFilter = (value) => {
        setSelectedOrg(value || null);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        if (value) fetchDepartments(value);
    };

    const handleDeptFilter = (value) => {
        setSelectedDept(value || null);
        setSelectedDesig(null);
        setDesignations([]);
        if (value) fetchDesignations(value);
    };

    const handleDesigFilter = (value) => {
        setSelectedDesig(value || null);
    };

    // ✅ Handle appraisal submit
    const handleSubmit = async () => {
        if (!SelectedAppId) {
            toast.error("Please select an appraisal cycle first.");
            return;
        }

        if (selectedEmpIds.length === 0) {
            toast.warning("Please select at least one employee.");
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                cycle_id: SelectedAppId,
                employee_ids: selectedEmpIds,
            };

            const res = await axios.post(
                `${backUrl}/api/emp/employees/start-appraisal-batch/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            if (res.status === 200 || res.status === 201) {
                toast.success("Appraisal started successfully!");
                setSelectedEmpIds([]);
            } else {
                toast.error("Unexpected response from server.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to start appraisal. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const rowSelection = {
        selectedRowKeys: selectedEmpIds,
        onChange: (selectedRowKeys) => setSelectedEmpIds(selectedRowKeys),
    };

    const columns = [
        { title: "Emp Code", dataIndex: "emp_code", key: "emp_code" },
        {
            title: "Name",
            key: "name",
            render: (_, record) => `${record.first_name || ""} ${record.last_name || ""}`,
        },
        {
            title: "Date Of Joining",
            key: "doj",
            render: (_, record) =>
                record.date_of_joining
                    ? new Date(record.date_of_joining).toLocaleDateString("en-GB")
                    : "-",
        },
        { title: "Department", dataIndex: "department_name", key: "department_name" },
        { title: "Designation", dataIndex: "designation_name", key: "designation_name" },
        { title: "Location", dataIndex: "location", key: "location" },
        { title: "Organization", dataIndex: "organisation_name", key: "organisation_name" },
    ];

    return (
        <>
            <Title
                level={4}
                style={{ color: "#0e497b", textAlign: "center", marginBottom: 20 }}
            >
                Employees for Appraisal
            </Title>

            {/* ✅ Filter Section */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
                <Space>
                    <span style={{ color: "#0e497b", fontWeight: 500 }}>Organization:</span>
                    <Select
                        placeholder="Select Organization"
                        allowClear
                        style={{ width: 250 }}
                        value={selectedOrg}
                        onChange={handleOrgFilter}
                    >
                        {organisations.map((org) => (
                            <Option key={org.id} value={org.id}>
                                {org.name}
                            </Option>
                        ))}
                    </Select>

                    <span style={{ color: "#0e497b", fontWeight: 500 }}>Department:</span>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        style={{ width: 250 }}
                        value={selectedDept}
                        onChange={handleDeptFilter}
                        disabled={!selectedOrg}
                    >
                        {departments.map((dept) => (
                            <Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Option>
                        ))}
                    </Select>

                    <span style={{ color: "#0e497b", fontWeight: 500 }}>Designation:</span>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        style={{ width: 250 }}
                        value={selectedDesig}
                        onChange={handleDesigFilter}
                        disabled={!selectedDept}
                    >
                        {designations.map((desig) => (
                            <Option key={desig.id} value={desig.id}>
                                {desig.name}
                            </Option>
                        ))}
                    </Select>
                </Space>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    <Table
                        dataSource={filteredEmployees}
                        columns={columns}
                        rowKey={(record) => record.id}
                        pagination={{ pageSize: 30 }}
                        rowSelection={rowSelection}
                        bordered
                        components={{
                            header: {
                                cell: (props) => (
                                    <th
                                        {...props}
                                        style={{
                                            backgroundColor: "#0e497b",
                                            color: "white",
                                        }}
                                    >
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                    />

                    <div style={{ textAlign: "center", marginTop: 20 }}>
                        <Button
                            type="primary"
                            style={{
                                backgroundColor: "#0e497b",
                                borderColor: "#0e497b",
                                fontWeight: 500,
                            }}
                            loading={submitting}
                            onClick={handleSubmit}
                        >
                            {submitting ? "Submitting..." : "Submit"}
                        </Button>
                    </div>

                    <ToastContainer position="top-right" autoClose={3000} />
                </>
            )}
        </>
    );
}
